/**
 * The published marketing site, and the links the app makes out to it.
 *
 * **One named origin per trust boundary.** About and legal documents stay on the public marketing
 * host. D284 moves the interactive closed-beta flow to its own host and deployment account, so it
 * cannot share that production boundary. Each host is still named once rather than copied by its
 * callers.
 *
 * **Why `pwa/` and not `screens/shared/`.** These are links *out* of the app to a separately
 * deployed site, which is the same class of thing `storeUpdate.ts` already models one directory
 * over — its `action: 'store'` means *the store listing opens over us*. `screens/` resolves to P7
 * in `web-ownership-map.ts` and this is not a screen's module; `pwa/` is P6's, and both callers
 * are P6-owned files, so nothing here crosses an ownership seam.
 *
 * The beta flow owns its two languages in one JavaScript table. The PWA selects Hebrew for `he`
 * and English for every other locale by a query parameter; the beta page validates that parameter
 * against the two supported values before it affects any rendered state.
 *
 * **D313 adds a third host, and it is the first one we do not deploy.** The iOS beta is
 * TestFlight's, so the door is Apple's invite link rather than a page of ours — which is why it
 * carries no locale and why there is no iOS twin of `beta-site/`. The rule is unchanged: one named
 * origin per trust boundary, each named exactly once.
 */

const MARKETING_HOST = 'www.selogate.com'
const BETA_URL = 'https://beta.selogate.com'

export const WEBSITE_URL = `https://${MARKETING_HOST}`

/**
 * The Hebrew pages are `.he.html`, not a `/he/` prefix — the site has no build step and no router,
 * so a page is a file. Every other locale falls through to the English one, deliberately.
 */
function pageUrl(page: string, locale: string): string {
  return `${WEBSITE_URL}/${page}${locale === 'he' ? '.he' : ''}.html`
}

/**
 * The two published legal documents. **`about` is a screen and these are not**, because About is
 * content we wrote for the app while these are the *published* documents, and a second in-app copy
 * of a legal text is a second version of it to keep in step.
 */
export function legalUrl(doc: 'privacy' | 'terms', locale: string): string {
  return pageUrl(doc, locale)
}

/**
 * D284's Android closed-beta onboarding flow: join the testers group, confirm tester status,
 * install from Play. It stays outside the app because it is read by someone in the act of leaving
 * for the store-delivered client, but it no longer runs under the public/legal site's trust boundary.
 *
 * **`flow=migrate` because every caller of this function is already a resident.** The beta page
 * serves two flows from one deploy: the bare URL is the plain join-and-install walkthrough, and
 * `?flow=migrate` adds the *did you send us your email?* question and, on the install button, a note
 * to remove the app already on the device once the new one is in. The PWA's prompt is only ever raised on Home, to someone signed in, so it
 * is the migrate audience by construction. `website/invite.html` is the other door to the same
 * host and deliberately does **not** go through here — an invitee has no app to remove, and that
 * page keeps the plain flow.
 */
export function androidBetaUrl(locale: string): string {
  return `${BETA_URL}/?flow=migrate&lang=${locale === 'he' ? 'he' : 'en'}`
}

/**
 * **D313's iOS half of the same campaign, and it is a THIRD named origin rather than a second
 * form of the beta host.** Apple runs the closed beta itself: an iOS resident cannot be handed a
 * page that says *install from the store*, because the store does not carry the build — TestFlight
 * does, and the invite link is the only door to it. So this points at `testflight.apple.com`
 * directly and `beta-site/` has no iOS counterpart to deploy.
 *
 * **No locale parameter, and that is Apple's page rather than an omission.** `androidBetaUrl()`
 * selects a language because the page behind it is ours and holds a two-language table; this one
 * is rendered by Apple in the device's own language, and appending a `lang` it does not read would
 * be a parameter that looks load-bearing and is not.
 *
 * **The join code is the whole address.** There is no account id, gate id, or resident identifier
 * in it — it is the same public link that travels in the invite mail — so nothing here resolves to
 * a person and D111's posture is untouched.
 */
const TESTFLIGHT_URL = 'https://testflight.apple.com/join/pcnDhkEz'

export function iosBetaUrl(): string {
  return TESTFLIGHT_URL
}
